import { useMutation } from '@tanstack/react-query';
import {
    CameraCCTVModel,
    CameraModel,
    ICameraCCTVModel,
    ICameraModel,
    IDocumentFileModel,
    IExternalLinkModel,
    IProjectModel,
    IUserModel,
    ProjectModel,
    UserModel,
} from 'api/models';
import { getSearchResults } from 'api/services';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SearchTypeEnum } from '../enums/searchbar.enums';

const useSearchQuery = () => {
    const [projects, setProjects] = useState<ProjectModel[]>([]);
    const [cameras, setCameras] = useState<CameraModel[]>([]);
    const [cameraCctvs, setCameraCctvs] = useState<CameraCCTVModel[]>([]);
    const [users, setUsers] = useState<UserModel[]>([]);
    const [files, setFiles] = useState<IDocumentFileModel[]>([]);
    const [externalLinks, setExternalLinks] = useState<IExternalLinkModel[]>([]);

    const ref = useRef(null);

    const searchProjectsMutation = useMutation((q: string) => getSearchResults<IProjectModel>('projects', q));
    const searchCamerasMutation = useMutation((q: string) => getSearchResults<ICameraModel>('cameras', q));
    const searchCameraCctvsMutation = useMutation((q: string) => getSearchResults<ICameraCCTVModel>('camera-cctvs', q));
    const searchUsersMutation = useMutation((q: string) => getSearchResults<IUserModel>('users', q));
    const searchFilesMutation = useMutation((q: string) => getSearchResults<IDocumentFileModel>('files', q));
    const searchExternalLinksMutation = useMutation((q: string) =>
        getSearchResults<IDocumentFileModel>('external-links', q)
    );

    const fetch = useCallback(
        (q: string) => {
            ref.current = q;

            searchProjectsMutation.mutateAsync(q).then((result) => {
                if (result.q === ref.current) setProjects(result.data.map((row) => ProjectModel.fromObject(row)));
            });

            searchCamerasMutation.mutateAsync(q).then((result) => {
                if (result.q === ref.current) setCameras(result.data.map((row) => CameraModel.fromObject(row)));
            });

            searchCameraCctvsMutation.mutateAsync(q).then((result) => {
                if (result.q === ref.current) setCameraCctvs(result.data.map((row) => CameraCCTVModel.fromObject(row)));
            });

            searchUsersMutation.mutateAsync(q).then((result) => {
                if (result.q === ref.current) setUsers(result.data.map((row) => UserModel.fromObject(row)));
            });
            searchFilesMutation.mutateAsync(q).then((result) => {
                if (result.q === ref.current) setFiles(result.data.map((row) => row as IDocumentFileModel));
            });
            searchExternalLinksMutation.mutateAsync(q).then((result) => {
                if (result.q === ref.current) setExternalLinks(result.data.map((row) => row as IExternalLinkModel));
            });
        },
        [
            searchProjectsMutation,
            searchCamerasMutation,
            searchCameraCctvsMutation,
            searchUsersMutation,
            searchFilesMutation,
            searchExternalLinksMutation,
        ]
    );

    const resetResults = useCallback(() => {
        setProjects([]);
        setCameras([]);
        setCameraCctvs([]);
        setUsers([]);
        setFiles([]);
        setExternalLinks([]);
    }, []);

    const groups = useMemo(() => {
        return [
            {
                type: SearchTypeEnum.PROJECT,
                label: 'Projects',
                isLoading: searchProjectsMutation.isLoading,
                data: projects,
            },
            {
                type: SearchTypeEnum.CAMERA,
                label: 'TimeLapse Cameras',
                isLoading: searchCamerasMutation.isLoading,
                data: cameras,
            },
            {
                type: SearchTypeEnum.CAMERA_CCTV,
                label: 'Live Cameras',
                isLoading: searchCameraCctvsMutation.isLoading,
                data: cameraCctvs,
            },
            {
                type: SearchTypeEnum.USER,
                label: 'Users',
                isLoading: searchUsersMutation.isLoading,
                data: users,
            },
            {
                type: SearchTypeEnum.FILES,
                label: 'Files',
                isLoading: searchFilesMutation.isLoading,
                data: files,
            },
            {
                type: SearchTypeEnum.EXTERNAL_LINK,
                label: 'External Links',
                isLoading: searchExternalLinksMutation.isLoading,
                data: externalLinks,
            },
        ];
    }, [
        searchProjectsMutation.isLoading,
        projects,
        searchCamerasMutation.isLoading,
        cameras,
        searchCameraCctvsMutation.isLoading,
        cameraCctvs,
        searchUsersMutation.isLoading,
        users,
        searchFilesMutation.isLoading,
        files,
        searchExternalLinksMutation.isLoading,
        externalLinks,
    ]);

    return {
        fetch,
        resetResults,
        groups,
        isEmpty: groups.every((group) => group.data.length === 0),
        isLoading: groups.some((group) => group.isLoading),
    };
};

export default useSearchQuery;
